/**
 * Single source of truth for blog categories and paging.
 *
 * These label maps used to be copy-pasted into pages/blog/index.vue,
 * pages/blog/[slug].vue and components/blog/BlogCard.vue — and had already
 * drifted apart ('Career' vs 'Career Development'). Import from here instead.
 */

/** Short labels, for category chips and card badges. */
export const BLOG_CATEGORIES: Record<string, string> = {
  'resume-cv': 'Resume & CV',
  'digital-business-card': 'Business Card',
  'personal-website': 'Personal Website',
  'linkedin': 'LinkedIn',
  'job-interview': 'Job Interview',
  'job-search': 'Job Search',
  'freelancing': 'Freelancing',
  'career-development': 'Career',
  'ai-technology': 'AI & Tech',
  'cover-letter': 'Cover Letter',
  'networking': 'Networking',
  'remote-work': 'Remote Work',
  'students-graduates': 'Students',
  'industry-specific': 'Industry',
  'entrepreneurship': 'Business',
}

/**
 * Long labels, for page titles, <h1>s and breadcrumbs, where the short form is
 * too terse to stand on its own. Falls back to the short label.
 */
export const BLOG_CATEGORY_TITLES: Record<string, string> = {
  'career-development': 'Career Development',
  'ai-technology': 'AI & Technology',
  'students-graduates': 'Students & Graduates',
  'industry-specific': 'Industry Specific',
  'entrepreneurship': 'Entrepreneurship',
}

/** Articles per listing page. Also drives the sitemap's page-URL count. */
export const BLOG_PAGE_SIZE = 20

/** True when the slug is a category we actually recognise. Unknown ones must 404. */
export function isBlogCategory(value: unknown): value is string {
  return typeof value === 'string' && Object.hasOwn(BLOG_CATEGORIES, value)
}

/** Short display label for a category slug. */
export function blogCategoryLabel(category?: string | null): string | null {
  if (!category) return null
  return BLOG_CATEGORIES[category] ?? category
}

/** Long display title for a category slug, for headings and breadcrumbs. */
export function blogCategoryTitle(category?: string | null): string | null {
  if (!category) return null
  return BLOG_CATEGORY_TITLES[category] ?? BLOG_CATEGORIES[category] ?? category
}

/**
 * Path segments under /blog that are real routes, so they can never be used as
 * an article slug. Nuxt sorts static segments ahead of dynamic ones, so a post
 * slugged 'page' would be permanently unreachable.
 */
export const RESERVED_BLOG_SLUGS = ['page', 'category'] as const

/**
 * Languages the blog is written in. Each post has one (`blog_posts.locale`) and
 * lives under that language's blog: English at /blog, Czech at /cs/blog, German
 * at /de/blog. The blogs are separate, not translations of each other, so a
 * post has no counterpart in another language. Categories exist in English only.
 */
export const BLOG_LOCALES = ['en', 'cs', 'de'] as const
export type BlogLocale = typeof BLOG_LOCALES[number]

export function isBlogLocale(value: unknown): value is BlogLocale {
  return typeof value === 'string' && (BLOG_LOCALES as readonly string[]).includes(value)
}

/** The blog a reader of this site language gets: their own, or the English one. */
export function blogLocaleFor(appLocale: unknown): BlogLocale {
  return isBlogLocale(appLocale) ? appLocale : 'en'
}

/** "/blog", "/cs/blog", "/de/blog". */
export function blogHomePath(locale: BlogLocale = 'en'): string {
  return locale === 'en' ? '/blog' : `/${locale}/blog`
}

/** "/blog/<slug>" for an English post, "/cs/blog/<slug>" for a Czech one, and so on. */
export function blogPostPath(post: { slug: string, locale?: string | null }): string {
  return `${blogHomePath(blogLocaleFor(post.locale))}/${post.slug}`
}

const DATE_LOCALES: Record<BlogLocale, string> = { en: 'en-US', cs: 'cs-CZ', de: 'de-DE' }

/** Dates on cards and articles, in the post's language. */
export function formatBlogDate(date: string, locale?: string | null): string {
  return new Date(date).toLocaleDateString(DATE_LOCALES[blogLocaleFor(locale)], {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/** Listing base path for a category (or the whole blog when omitted). */
export function blogBasePath(category?: string | null, locale: BlogLocale = 'en'): string {
  return category ? `/blog/category/${category}` : blogHomePath(locale)
}

/**
 * URL of a listing page. Page 1 is always the bare base path — /blog/page/1 is a
 * duplicate of /blog and 301s there.
 */
export function blogPageUrl(basePath: string, page: number): string {
  return page <= 1 ? basePath : `${basePath}/page/${page}`
}
